import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/sidebar/Sidebar';
import movieService, { MovieProjectSummary } from '../../services/api/movieService';
import { Clapperboard, Plus, Trash2, Film, CheckCircle2, Loader2 } from 'lucide-react';
import { ResponsiveAppShell, TaskyTopBar, useConfirm, TaskyPageContainer } from '../../components/tasky';
import { useToast } from '../../contexts/ToastContext';

/**
 * ムービー制作（β）プロジェクト一覧ページ。
 *
 * 単発生成型の既存ページ（バナー/動画広告）と異なり、
 * 「プロジェクト」を作成してエディタ（/ads/movie/:id）で
 * 台本 → 絵コンテ → クリップ → 結合 の4段階を進める。
 */

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  draft: { label: '制作中', className: 'bg-gray-100 text-gray-600' },
  concatenating: { label: '結合中', className: 'bg-blue-100 text-blue-700' },
  completed: { label: '完成', className: 'bg-green-100 text-green-700' },
  concat_failed: { label: '結合失敗', className: 'bg-red-100 text-red-700' },
};

const MovieCreationPage: React.FC = () => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [projects, setProjects] = useState<MovieProjectSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 新規作成モーダル
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  const fetchProjects = useCallback(async () => {
    try {
      const data = await movieService.getProjects();
      setProjects(data);
    } catch (e) {
      setError(movieService.extractErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleCreate = async () => {
    if (!newTitle.trim() || isCreating) return;
    setIsCreating(true);
    setError(null);
    try {
      const id = await movieService.createProject({ title: newTitle.trim() });
      showToast('ムービープロジェクトを作成しました');
      navigate(`/ads/movie/${id}`);
    } catch (e) {
      setError(movieService.extractErrorMessage(e));
      setIsCreating(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent, project: MovieProjectSummary) => {
    e.stopPropagation();
    if (!(await confirm({ title: `「${project.title}」を削除しますか？シーン・生成履歴との紐付けも解除されます。`, tone: 'destructive', confirmText: '削除する' }))) {
      return;
    }
    try {
      await movieService.deleteProject(project.id);
      setProjects((prev) => prev.filter((p) => p.id !== project.id));
    } catch (err) {
      setError(movieService.extractErrorMessage(err));
    }
  };

  return (
    <ResponsiveAppShell
      sidebar={<Sidebar collapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />}
      mobileSidebar={<Sidebar collapsed={false} onToggleCollapse={() => undefined} />}
      topbarTitle="ムービー制作（β）"
      topbarActions={(
        <button
          onClick={() => {
            setNewTitle('');
            setShowCreateModal(true);
          }}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-primary hover:bg-primary/10"
          aria-label="新規プロジェクト"
          type="button"
        >
          <Plus className="w-4 h-4" />
        </button>
      )}
    >
      <div className="flex min-h-[calc(100dvh-56px)] flex-col overflow-hidden bg-white lg:h-screen lg:min-h-0">
        {/* Header */}
        <TaskyTopBar
          title="ムービー制作（β）"
          icon={<Clapperboard className="w-5 h-5" />}
          sidebarCollapsed={sidebarCollapsed}
          onExpandSidebar={() => setSidebarCollapsed(false)}
        >
          <button
            data-tutorial="ads-movie-new"
            onClick={() => {
              setNewTitle('');
              setShowCreateModal(true);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-sm rounded-lg hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            新規プロジェクト
          </button>
        </TaskyTopBar>

        {/* Main */}
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <TaskyPageContainer>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
                {error}
              </div>
            )}

            <div data-tutorial="ads-movie-list">
            {isLoading ? (
              <div className="flex items-center justify-center py-20 text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
                読み込み中...
              </div>
            ) : projects.length === 0 ? (
              <div className="text-center py-20 text-gray-400">
                <Film className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p className="text-sm">プロジェクトがありません</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {projects.map((project) => {
                  const status = STATUS_LABELS[project.status] ?? STATUS_LABELS.draft;
                  return (
                    <div
                      key={project.id}
                      onClick={() => navigate(`/ads/movie/${project.id}`)}
                      className="border border-warm-border rounded-xl overflow-hidden cursor-pointer hover:shadow-md transition-shadow bg-white"
                    >
                      <div className="aspect-video bg-gray-100 flex items-center justify-center overflow-hidden">
                        {project.thumbnail_url ? (
                          <img
                            src={project.thumbnail_url}
                            alt={project.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Film className="w-10 h-10 text-gray-300" />
                        )}
                      </div>
                      <div className="p-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <h3 className="text-sm font-semibold text-gray-900 truncate">{project.title}</h3>
                          <button
                            onClick={(e) => handleDelete(e, project)}
                            className="p-1 text-gray-400 hover:text-red-500 shrink-0"
                            title="削除"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span className={`px-1.5 py-0.5 rounded ${status.className}`}>{status.label}</span>
                          <span>{project.scene_count}シーン</span>
                          {project.scene_count > 0 && (
                            <span className="flex items-center gap-0.5">
                              <CheckCircle2 className="w-3 h-3" />
                              絵コンテ{project.storyboard_done_count} / クリップ{project.clip_done_count}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            </div>
          </TaskyPageContainer>
        </div>
      </div>

      {/* 新規作成モーダル */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3">
          <div className="max-h-[calc(100dvh-24px)] w-full max-w-md overflow-y-auto rounded-xl bg-white p-4 sm:p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">新規プロジェクト</h2>
            <label className="block text-sm text-gray-700 mb-1">プロジェクト名</label>
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
              }}
              placeholder="例: 新商品プロモーション動画"
              maxLength={100}
              autoFocus
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                キャンセル
              </button>
              <button
                onClick={handleCreate}
                disabled={!newTitle.trim() || isCreating}
                className="px-4 py-2 text-sm bg-primary text-white rounded-lg disabled:opacity-50 hover:opacity-90"
              >
                {isCreating ? '作成中...' : '作成してエディタへ'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ResponsiveAppShell>
  );
};

export default MovieCreationPage;
